import React, { useState } from 'react'
import { Box, Paper, Typography, useTheme, CircularProgress } from '@mui/material'
import { RankingResult, getCategoryConfig } from '../../hooks/useBacktestRankings'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import LockIcon from '@mui/icons-material/Lock'
import UpgradeDialog from '../UpgradeDialog'

interface RankCardProps {
  rank: number
  group: string
  profitPercentage: number
  currentProfit: number
  closedTradeCount: number
  winRate: number
  category: 'expectancy' | 'robust' | 'riskAdjusted'
  categoryValue: number
  categoryDescription: string
  badge: string
  loading?: boolean
  locked?: boolean
}

function RankCard({
  group,
  category,
  categoryValue,
  categoryDescription,
  badge,
  loading,
  locked,
}: RankCardProps) {
  const theme = useTheme()
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false)

  const categoryConfig = getCategoryConfig(category, theme)

  const formatCurrency = (value: number) => {
    return `$${Math.abs(value).toFixed(2)}`
  }

  return (
    <Paper
      elevation={0}
      sx={{
        py: 2,
        px: 2,
        backgroundColor: theme.palette.background.custom2,
        borderRadius: 2,
        border: `2px solid ${categoryConfig.color}`,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minWidth: '280px',
        flex: '1 1 280px',
        maxWidth: '400px',
      }}
    >
      {/* Category Badge with Title */}
      <Box
        sx={{
          position: 'absolute',
          top: -12,
          left: 16,
          backgroundColor: categoryConfig.color,
          color: 'white',
          borderRadius: '16px',
          padding: '4px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: '0.75rem',
          fontWeight: 'bold',
        }}
      >
        {categoryConfig.icon}
        <Typography
          variant="body2"
          sx={{
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: 'bold',
          }}
        >
          {badge}
        </Typography>
      </Box>

      {/* Ratio Display on Right */}
      <Box
        sx={{
          position: 'absolute',
          top: -12,
          right: 16,
          backgroundColor: 'background.custom2',
          border: `1px solid ${categoryConfig.color}`,
          color: 'white',
          borderRadius: '16px',
          padding: '4px 12px',
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.75rem',
          fontWeight: 'bold',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: 'bold',
          }}
        >
          {locked || (loading && !group) ? 'Risk/Reward' : group}
        </Typography>
      </Box>

      {/* Header - Description and Value */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 'fit-content' }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            flex: 1,
            minWidth: 0, // Allows text to wrap
            wordBreak: 'break-word',
            lineHeight: 1.4,
          }}
        >
          {categoryDescription}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: categoryConfig.color,
            fontWeight: 'bold',
            flexShrink: 0, // Prevents value from shrinking
            whiteSpace: 'nowrap', // Prevents value from wrapping
            alignSelf: 'center', // Centers vertically within the line
          }}
        >
          {locked ? (
            <LockIcon
              fontSize="small"
              color="disabled"
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.7,
                },
              }}
              onClick={() => setUpgradeDialogOpen(true)}
            />
          ) : loading && !categoryValue ? (
            <CircularProgress size={16} sx={{ color: categoryConfig.color }} />
          ) : category === 'expectancy' ? (
            `${categoryValue.toFixed(2)}`
          ) : category === 'robust' ? (
            `${categoryValue.toFixed(1)}`
          ) : category === 'riskAdjusted' ? (
            categoryValue >= 100 ? (
              '∞'
            ) : (
              categoryValue.toFixed(2)
            )
          ) : (
            formatCurrency(categoryValue)
          )}
        </Typography>
      </Box>

      <UpgradeDialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        marketplaceSection="membership"
      />
    </Paper>
  )
}

interface RankCardsProps {
  backtest: any
  monteCarloLoading?: boolean
  rankingResults?: RankingResult[]
}

export default function RankCards({ backtest, monteCarloLoading, rankingResults }: RankCardsProps) {
  const theme = useTheme()

  const { canViewBacktestInsights } = useApplicationSuiteContext()

  // Use ranking results from the hook
  const rankedSummaries = React.useMemo(() => {
    if (!rankingResults || rankingResults.length === 0) return []

    return rankingResults
      .map((result, index) => {
        // Find the corresponding summary
        const summary = backtest?.summaries?.find((s: any) => s.group === result.group)

        return {
          summary,
          category: result.category,
          categoryValue: result.value,
          categoryDescription: result.description,
          badge: result.badge,
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
  }, [rankingResults, backtest?.summaries])

  if (rankedSummaries.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          backgroundColor: theme.palette.background.custom2,
          borderRadius: 2,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No trade data available
        </Typography>
      </Paper>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 2,
        flex: 1,
        height: '100%',
        alignItems: 'flex-start',
      }}
    >
      {rankedSummaries.map((item, index) => {
        const summary = item.summary
        const winRate =
          summary?.closedTradeCount > 0
            ? (summary?.profitTradeCount / summary?.closedTradeCount) * 100
            : 0

        return (
          <RankCard
            key={`${item.category}-${summary?.group}`}
            rank={index + 1}
            group={summary?.group || ''}
            profitPercentage={summary?.profitPercentage || 0}
            currentProfit={summary?.currentProfit || 0}
            closedTradeCount={summary?.closedTradeCount || 0}
            winRate={winRate}
            category={item.category}
            categoryValue={item.categoryValue}
            categoryDescription={item.categoryDescription}
            badge={item.badge}
            loading={monteCarloLoading}
            locked={!canViewBacktestInsights && item.category === 'robust'}
          />
        )
      })}
    </Box>
  )
}
